import React from "react";

import { useQuery, useSubscription } from "@apollo/client";
import LockIcon from "@mui/icons-material/Lock";
import SmsIcon from "@mui/icons-material/Sms";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { differenceInYears, formatDistance } from "date-fns";
import { Maybe } from "graphql/jsutils/Maybe";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import PersonAddIcon from "@/components/Icons/PersonAddIcon";
import Visibility from "@/components/layout/Visibility";
import NavLink from "@/components/NavLink";
import { unreadMessagesSubscription } from "@/components/Sidebar/Sidebar";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { Gender } from "@/gql/graphql";
import useBooleanToggle from "@/hooks/useBooleanToggle";
import { colors } from "@/theme/overrides/colors";

import BillingStatus from "./BillingStatus";
import InviteClientDialog from "./InviteClientDialog";

const useStyles = makeStyles()((theme: Theme) => ({
  section: {
    margin: theme.spacing(4, 2, 4, 2),
    padding: "24px 24px 0 24px",
    backgroundColor: colors.white,
  },
  avatar: {
    height: 64,
    width: 64,
    marginRight: theme.spacing(4),
  },
  tabs: {
    marginTop: theme.spacing(2),
    display: "flex",
    // Allow the tab row to scroll horizontally on narrow screens instead of
    // overflowing / clipping tabs.
    overflowX: "auto",
    "&::-webkit-scrollbar": { display: "none" },
    scrollbarWidth: "none",
  },
  link: {
    textDecoration: "none",
    color: "inherit",
    width: "100%",
    height: 48,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2, 4),
    // On mobile let tabs keep their natural width and scroll horizontally
    // instead of being squeezed to fit.
    [theme.breakpoints.down("md")]: {
      width: "auto",
      flexShrink: 0,
      whiteSpace: "nowrap",
    },
  },
  activeLink: {
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  gender: {
    textTransform: "none",
  },
  inviteButton: {
    color: "#2D87BB",
  },
  inviteButtonLabel: {
    textTransform: "none",
  },
  inviteButtonIcon: {
    fontSize: 16,
    marginRight: 7,
  },
  inactive: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(6, 8, 6, 5),
    borderLeft: "6px solid #ff9d37",
  },
  inactiveIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    borderRadius: "50%",
    backgroundColor: `${theme.palette.secondary.main}1a`,
  },
  inactiveMessage: {
    margin: theme.spacing(0, 4),
    maxWidth: 890,
  },
  inactiveTitle: {
    marginBottom: theme.spacing(3),
    color: colors.secondary,
    fontSize: 22,
    fontWeight: 600,
  },
  inactiveBody: {
    color: colors.gray,
    fontSize: 14,
  },
  inactiveUpgradePlan: {
    flexShrink: 0,
    marginLeft: "auto",
  },
  messageWrapper: {
    display: "flex",
    alignItems: "center",
  },
  dividerGradient: {
    alignSelf: "stretch",
    width: 1,
    margin: theme.spacing(0, 4),
    background:
      "linear-gradient(180deg, rgba(192,197,205,0) 0%, rgba(192,197,205,1) 50%, rgba(192,197,205,0) 100%)",
    [theme.breakpoints.down("md")]: {
      margin: theme.spacing(0, 1.5),
    },
  },
}));

const headerSectionQuery = graphql(`
  query ClientHeaderSection($id: ID!) {
    coachViewer {
      id
      activeClientsLimitReached
    }

    client(id: $id) {
      id
      email
      account {
        id
      }
      chat {
        id
        viewerUnreadMessagesCount
      }
      profile {
        id
        fullName
        gender
        dateOfBirth
        mobileNumber
        avatarUrl(variant: THUMB)
      }
      lastActivityAt
      invitedAt
      status
    }
  }
`);

const HeaderSection: React.FC = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const params = useParams();
  const { data } = useQuery(headerSectionQuery, {
    variables: { id: params.clientId ?? "" },
  });
  useSubscription(unreadMessagesSubscription);
  const me = data?.coachViewer;
  const profile = data?.client?.profile;
  const isGhost = data?.client?.account?.id == null;
  const diaryUrl = `diary`;
  const [isInviteDialogOpen, inviteDialogOpen] = useBooleanToggle(false);
  const ProfileUrl = `profile`;
  const PlanUrl = `programs`;
  const MessageUrl = `messages/appointment`;
  const clientAge =
    profile?.dateOfBirth != undefined
      ? differenceInYears(new Date(), new Date(profile.dateOfBirth)) + " years"
      : "N/A";

  function getHumanReadableLastActivity(
    lastActivity: Maybe<string> | undefined
  ) {
    if (!lastActivity) return "N/A";
    return formatDistance(new Date(lastActivity), new Date(), {
      addSuffix: true,
    }); //3 days ago
  }
  return (
    <div>
      {me?.activeClientsLimitReached && data?.client?.status === "INACTIVE" && (
        <section className={clsx(classes.section, classes.inactive)}>
          <div className={classes.inactiveIcon}>
            <LockIcon color="secondary" />
          </div>
          <div className={classes.inactiveMessage}>
            <Typography variant="h6" className={classes.inactiveTitle}>
              It&apos;s not possible to edit current client&apos;s information
            </Typography>
            <Typography variant="body1" className={classes.inactiveBody}>
              Oh No! You&apos;ve reached the limit of your maximum allowed
              Active Clients per month. You can increase your monthly limit by
              upgrading your plan.
            </Typography>
          </div>
          <Button
            className={classes.inactiveUpgradePlan}
            color="secondary"
            component={NavLink}
            to="/subscription"
          >
            Upgrade Plan
          </Button>
        </section>
      )}
      <section className={classes.section}>
        {/* Avatar */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" alignItems="center">
            <div>
              <Avatar
                className={classes.avatar}
                src={profile?.avatarUrl ?? ""}
              />
            </div>
            <Box display="flex" alignItems="flex-start" flexDirection="column">
              <Typography variant="body1">{profile?.fullName}</Typography>
              <Typography variant="caption" color="textSecondary">
                {/* No age yet */}
                {`${clientAge} | ${
                  profile?.gender === Gender.Female ? "Female" : "Male"
                }`}
              </Typography>
              {!Features.clientProfileFieldsEnabled && (
                <Typography variant="caption" color="textSecondary">
                  {`Active ${getHumanReadableLastActivity(
                    data?.client?.lastActivityAt
                  )}`}
                </Typography>
              )}
            </Box>
          </Box>
          {Features.clientProfileFieldsEnabled && (
            <>
              <div>
                <div>
                  <Typography
                    display="inline"
                    variant="body2"
                    color="textSecondary"
                  >
                    Program:
                  </Typography>
                  <Typography align="right" display="inline" variant="body2">
                    {"Diebetic Diet Plan"}
                  </Typography>
                </div>
                <div>
                  <Typography
                    display="inline"
                    variant="body2"
                    color="textSecondary"
                  >
                    Package Status:
                  </Typography>
                  <Typography align="right" display="inline" variant="body2">
                    {"Active"}
                  </Typography>
                </div>
              </div>

              <div>
                <div>
                  <Typography
                    display="inline"
                    variant="body2"
                    color="textSecondary"
                  >
                    Last Activity:
                  </Typography>
                  <Typography align="right" display="inline" variant="body2">
                    {getHumanReadableLastActivity(data?.client?.lastActivityAt)}
                  </Typography>
                </div>
                <div>
                  <Typography
                    display="inline"
                    variant="body2"
                    color="textSecondary"
                  >
                    Phone:
                  </Typography>
                  <Typography align="right" display="inline" variant="body2">
                    {`${profile?.mobileNumber ?? "N/A"}`}
                  </Typography>
                </div>
              </div>
            </>
          )}
          <div className={classes.messageWrapper}>
            <Visibility visible={!!data?.client?.status}>
              <BillingStatus status={data?.client?.status} />
            </Visibility>
            <div className={classes.dividerGradient} />
            <Button
              disabled={isGhost}
              component={NavLink}
              to={`/messages/${data?.client?.chat.id}`}
              aria-label="Message"
              sx={{
                minWidth: { xs: 44, md: 64 },
                height: { xs: 44, md: "auto" },
                px: { xs: 1, md: 2 },
              }}
            >
              {isMobile ? <SmsIcon fontSize="small" /> : "Message"}
            </Button>
          </div>
        </Box>
        <div className={classes.tabs}>
          <Box display="flex" sx={{ flexShrink: 0 }}>
            <NavLink
              to={ProfileUrl}
              className={classes.link}
              activeClassName={classes.activeLink}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Profile
              </Typography>
            </NavLink>
            <NavLink
              to={diaryUrl}
              className={classes.link}
              activeClassName={classes.activeLink}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Diary
              </Typography>
            </NavLink>
            <NavLink
              to={PlanUrl}
              className={classes.link}
              activeClassName={classes.activeLink}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Programs
              </Typography>
            </NavLink>
            {!isGhost && (
              <NavLink
                to={MessageUrl}
                className={classes.link}
                activeClassName={classes.activeLink}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  Messages
                </Typography>
                {data.client?.chat.viewerUnreadMessagesCount !== 0 && (
                  <Badge
                    style={{ paddingLeft: 14 }}
                    badgeContent={data.client?.chat.viewerUnreadMessagesCount}
                    color="primary"
                  />
                )}
              </NavLink>
            )}
            <NavLink
              to="modules"
              className={classes.link}
              activeClassName={classes.activeLink}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Modules
              </Typography>
            </NavLink>
            <NavLink
              to="reports"
              className={classes.link}
              activeClassName={classes.activeLink}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Reports
              </Typography>
            </NavLink>
          </Box>
          <Box flex={1}>
            {/* spacer to separate left tabs with right invite button */}
          </Box>
          <Box display="flex" alignItems="center" marginRight="-8px">
            {isGhost && (
              <Button
                className={classes.inviteButton}
                classes={{ text: classes.inviteButtonLabel }}
                variant="text"
                onClick={inviteDialogOpen.setTrue}
              >
                <PersonAddIcon className={classes.inviteButtonIcon} />
                {data?.client?.invitedAt == null
                  ? "Invite to download app"
                  : "Resend invite to download app"}
              </Button>
            )}
          </Box>
        </div>
        <InviteClientDialog
          open={isInviteDialogOpen}
          onClose={inviteDialogOpen.setFalse}
          clientId={data?.client?.id ?? ""}
          defaultEmail={data?.client?.email ?? undefined}
          afterInvite={inviteDialogOpen.setFalse}
        />
      </section>
    </div>
  );
};
export default HeaderSection;
